<template>
  <v-dialog
    :model-value="open"
    persistent
    max-width="440"
    scrim="rgba(15, 23, 42, 0.55)"
    content-class="session-expired-dialog"
    @keydown.esc.prevent
  >
    <v-card rounded="lg" class="session-expired-card">
      <v-card-title class="d-flex align-center ga-2 pt-5 px-5">
        <v-avatar color="warning" variant="tonal" size="40">
          <v-icon icon="mdi-lock-clock" />
        </v-avatar>
        <div>
          <div class="text-h6">Session expired</div>
          <div class="text-caption text-medium-emphasis font-weight-regular">
            Sign in again to continue without losing your place.
          </div>
        </div>
      </v-card-title>

      <v-card-text class="px-5 pb-2">
        <v-form v-if="stage === 'credentials'" @submit.prevent="onLogin">
          <v-text-field
            v-model="username"
            label="Username"
            prepend-inner-icon="mdi-account-outline"
            autocomplete="username"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            class="mb-3"
            autofocus
          />
          <v-text-field
            v-model="password"
            label="Password"
            type="password"
            prepend-inner-icon="mdi-lock-outline"
            autocomplete="current-password"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            class="mb-3"
          />
          <v-alert v-if="error" type="error" density="compact" variant="tonal" class="mb-3">
            {{ error }}
          </v-alert>
          <v-btn
            type="submit"
            color="primary"
            block
            size="large"
            class="text-none"
            height="44"
            :loading="busy"
            :disabled="!username.trim() || !password"
          >
            Sign in
          </v-btn>
        </v-form>

        <v-form v-else @submit.prevent="onMfa">
          <p class="text-body-2 text-medium-emphasis mb-3">
            Enter the 6-digit code from your authenticator app.
          </p>
          <v-text-field
            v-model="mfaCode"
            label="Authenticator code"
            prepend-inner-icon="mdi-shield-key-outline"
            autocomplete="one-time-code"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            class="mb-3"
            autofocus
          />
          <v-alert v-if="error" type="error" density="compact" variant="tonal" class="mb-3">
            {{ error }}
          </v-alert>
          <v-btn
            type="submit"
            color="primary"
            block
            size="large"
            class="text-none"
            height="44"
            :loading="busy"
            :disabled="mfaCode.trim().length < 6"
          >
            Verify
          </v-btn>
          <v-btn
            variant="text"
            block
            class="text-none mt-2"
            :disabled="busy"
            @click="stage = 'credentials'"
          >
            Back
          </v-btn>
        </v-form>
      </v-card-text>

      <v-card-actions class="px-5 pb-4">
        <v-btn variant="text" class="text-none text-medium-emphasis" :disabled="busy" @click="goFullLogin">
          Full login page
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { APP_BASE } from '@/config/deploy'

const auth = useAuthStore()
const router = useRouter()

const open = ref(false)
const busy = ref(false)
const error = ref('')
const username = ref('')
const password = ref('')
const mfaCode = ref('')
const stage = ref<'credentials' | 'mfa'>('credentials')

function setAppBlur(on: boolean) {
  // Blur #app only — Vuetify dialogs teleport to <body>, so the modal stays sharp
  const root = document.getElementById('app')
  if (!root) return
  if (on) root.classList.add('app-session-blur')
  else root.classList.remove('app-session-blur')
}

function onSessionExpired() {
  // Already on login route — leave full-page login alone
  if (router.currentRoute.value.name === 'login') return
  if (open.value) return
  error.value = ''
  password.value = ''
  mfaCode.value = ''
  stage.value = 'credentials'
  if (auth.user?.username) {
    username.value = String(auth.user.username)
  }
  open.value = true
  // Keep route; clear local auth so requests re-auth cleanly after login
  auth.clearSessionLocal()
}

async function onLogin() {
  error.value = ''
  busy.value = true
  try {
    const result = await auth.login(username.value.trim(), password.value)
    if (result === true) {
      open.value = false
      password.value = ''
      return
    }
    if (result && typeof result === 'object' && result.stage === 'mfa') {
      stage.value = 'mfa'
      mfaCode.value = ''
      return
    }
    error.value = auth.error || 'Login failed'
  } finally {
    busy.value = false
  }
}

async function onMfa() {
  error.value = ''
  busy.value = true
  try {
    const ok = await auth.verifyMfa(mfaCode.value.trim())
    if (ok) {
      open.value = false
      password.value = ''
      mfaCode.value = ''
      stage.value = 'credentials'
      return
    }
    error.value = auth.error || 'Invalid code'
  } finally {
    busy.value = false
  }
}

function goFullLogin() {
  open.value = false
  const redirect = router.currentRoute.value.fullPath
  window.location.href = `${APP_BASE}#/login?redirect=${encodeURIComponent(redirect)}`
}

watch(open, (v) => setAppBlur(v))

onMounted(() => {
  window.addEventListener('appcenter:session-expired', onSessionExpired)
})
onUnmounted(() => {
  window.removeEventListener('appcenter:session-expired', onSessionExpired)
  setAppBlur(false)
})
</script>

<style>
/* Blur + dim the SPA behind the re-auth modal (dialog is teleported outside #app) */
#app.app-session-blur {
  filter: blur(7px) brightness(0.85);
  pointer-events: none;
  user-select: none;
  transition: filter 0.2s ease;
}
.session-expired-dialog {
  z-index: 3000 !important;
}
.session-expired-card {
  pointer-events: auto;
}
</style>
